import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Icon } from "../../components/ui";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useTriggersForEntities } from "./useTriggers";
import { TriggerDetailPanel } from "./TriggerDetailPanel";
import type { TriggerListItem } from "./types";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500",
  paused: "bg-yellow-500",
  expired: "bg-content-muted",
  disabled: "bg-content-muted",
};

function TriggerRow({
  trigger,
  onSelect,
  isLast,
}: {
  readonly trigger: TriggerListItem;
  readonly onSelect: (id: string) => void;
  readonly isLast: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => { onSelect(trigger.id); }}
      className={`flex items-center gap-3 w-full px-3 py-2.5 cursor-pointer hover:bg-surface-hover transition-colors ${
        !isLast ? "border-b border-edge" : ""
      }`}
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[trigger.status] ?? "bg-content-muted"}`} />
      <div className="flex-1 min-w-0 text-left">
        <span className="text-[13px] font-medium text-agent-text block truncate">{trigger.name}</span>
        {trigger.firing_count > 0 && (
          <span className="text-[11px] text-agent-text-muted block">
            Fired {trigger.firing_count} time{trigger.firing_count !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <Icon name="chevron-right" size={14} className="text-agent-text-muted shrink-0" />
    </button>
  );
}

export function TriggersSection({
  entityIds,
  directTriggerIds = [],
}: {
  readonly entityIds: readonly string[];
  readonly directTriggerIds?: readonly string[];
}): JSX.Element | null {
  // Skip entity-based search if we already have direct trigger IDs
  const { triggers: foundTriggers, loading, deleteTrigger, setStatus, reload } =
    useTriggersForEntities(directTriggerIds.length > 0 ? [] : entityIds);
  const runtime = useAppRuntime();
  const [directTriggers, setDirectTriggers] = useState<TriggerListItem[]>([]);

  // Load triggers directly by ID (for triggers linked to episode via belongs_to)
  useEffect(() => {
    if (directTriggerIds.length === 0) return;
    void Promise.all(
      directTriggerIds.map((id) =>
        runtime.transport
          .rpc<TriggerListItem>("triggers.get", { id })
          .catch(() => null),
      ),
    ).then((results) => {
      setDirectTriggers(results.filter((t): t is TriggerListItem => t !== null));
    });
  }, [directTriggerIds, runtime]);

  // Merge and deduplicate
  const allTriggers = ((): TriggerListItem[] => {
    const seen = new Set<string>();
    const merged: TriggerListItem[] = [];
    for (const t of [...foundTriggers, ...directTriggers]) {
      if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
    }
    return merged;
  })();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (entityIds.length === 0 && directTriggerIds.length === 0) return null;
  if (loading || allTriggers.length === 0) return null;

  if (selectedId) {
    return (
      <TriggerDetailPanel
        triggerId={selectedId}
        onBack={() => {
          setSelectedId(null);
          void reload();
        }}
        onDelete={async () => {
          await deleteTrigger(selectedId);
          setSelectedId(null);
        }}
        onToggleStatus={async (status) => {
          await setStatus(selectedId, status);
        }}
      />
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold tracking-wider text-agent-text-muted uppercase">
          Triggers
        </span>
      </div>
      <div className="rounded-lg bg-agent-surface border border-agent-border overflow-hidden">
        {allTriggers.map((t, i) => (
          <TriggerRow
            key={t.id}
            trigger={t}
            onSelect={setSelectedId}
            isLast={i === allTriggers.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
